import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { StockCheckLogo } from "@/components/Logo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const sessionId = hash.split("session_id=")[1]?.split("&")[0];

        if (!sessionId) {
          toast.error("Authentication failed - no session ID");
          navigate("/login");
          return;
        }

        // Exchange session_id for user data
        const response = await fetch(
          "https://demobackend.emergentagent.com/auth/v1/env/oauth/session-data",
          {
            headers: {
              "X-Session-ID": sessionId,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to get session data");
        }

        const data = await response.json();

        // Send to our backend to create/update user and get JWT token
        const backendResponse = await fetch(`${API_URL}/api/auth/google`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: data.email,
            name: data.name,
            picture: data.picture,
            session_token: data.session_token,
          }),
          credentials: "include",
        });

        if (!backendResponse.ok) {
          throw new Error("Backend authentication failed");
        }

        const userData = await backendResponse.json();

        // Store token and user data
        localStorage.setItem("token", userData.token);
        localStorage.setItem("user", JSON.stringify(userData.user));

        toast.success(`Welcome, ${userData.user.name}!`);
        navigate("/", { replace: true, state: { user: userData.user } });
      } catch (error) {
        console.error("Auth callback error:", error);
        toast.error("Authentication failed. Please try again.");
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
      <div className="grid-background absolute inset-0 pointer-events-none opacity-30" />
      <div className="relative text-center">
        <StockCheckLogo size="large" showText={true} />
        <div className="mt-8 flex items-center justify-center gap-3">
          <span className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
          <span className="text-slate-400">Completing sign in...</span>
        </div>
      </div>
    </div>
  );
}
